import React, { useState, useEffect } from 'react'
import Container from '@mui/material/Container'
import { useTranslation } from 'react-i18next'
import { Search } from '@mui/icons-material'
import { useFormikContext } from 'formik'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'

import isAxiosError from 'shared/utils/helpers/isAxiosError'
import getCompanyData from 'shared/services/getCompanyData'
import useCountriesQuery from 'shared/store/useCountries'
import SelectField from 'shared/components/SelectField'
import InputField from 'shared/components/InputField'

type Option = {
   name: string
   value: string
}

export function SecondStep() {
   const { t } = useTranslation('TRANSLATION')
   const { setFieldError, setFieldValue, setValues, values, errors, setTouched } =
      useFormikContext<RegisterInitialValues>()
   const { data } = useCountriesQuery()
   const [options, setOptions] = useState<{ voivodeshipOptions: Option[]; countyOptions: Option[] }>({
      voivodeshipOptions: [],
      countyOptions: [],
   })
   const [isCompanyDataFetching, setIsCompanyDataFetching] = useState(false)
   const taxId = values.taxID
   const isError = !!errors.taxID

   async function handleOnSearch() {
      setIsCompanyDataFetching(true)
      await getCompanyData(taxId)
         .then((res) => {
            if (res) {
               setValues({
                  ...values,
                  voivodeship: res.voivodeship,
                  houseNumber: res.houseNumber,
                  companyName: res.companyName,
                  city: res.city,
                  street: res.street,
                  postCode: res.postalCode,
               })
            }
            setTimeout(() => {
               setFieldValue('county', res.county)
            }, 50)

            const allFieldsTouched: { [key: string]: boolean } = Object.keys(values).reduce(
               (acc, key) => {
                  acc[key] = true
                  return acc
               },
               {} as { [key: string]: boolean }
            )

            setTouched(allFieldsTouched)
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               setFieldError('taxID', t(`TRANSLATION:${error.response?.data?.errors[0].code}`))
            } else {
               setFieldError('taxID', error)
            }
         })
         .finally(() => setIsCompanyDataFetching(false))
   }

   useEffect(() => {
      if (data && values.country && values.country === 'Poland') {
         const selectedCountry = data[values.country]

         if (selectedCountry) {
            setOptions({
               voivodeshipOptions: Object.keys(selectedCountry.voivodeships).map((item) => ({
                  name: item,
                  value: item,
               })),
               countyOptions: values.voivodeship
                  ? selectedCountry.voivodeships[values.voivodeship].map((item) => ({
                       name: item,
                       value: item,
                    }))
                  : [],
            })
         }
      }
   }, [data, values])

   useEffect(() => {
      if (data && values.country === 'Poland') {
         const selectedCountry = data[values.country]

         if (!values.county || !selectedCountry?.voivodeships[values.voivodeship].includes(values.county)) {
            setFieldValue('county', '')
         }
      }
   }, [values.voivodeship])

   return (
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
         {/* {values.country && data ? ( */}
         <>
            <Grid container>
               <Grid size={12}>
                  <InputField<RegisterInitialValues>
                     label={t('formFields.taxID')}
                     name="taxID"
                     InputProps={{
                        endAdornment: values.country === 'Poland' && (
                           <Button
                              onClick={handleOnSearch}
                              color="primary"
                              variant="contained"
                              sx={{ height: 40, ml: 1.5 }}
                              disabled={isError || isCompanyDataFetching}
                           >
                              <Box component={Search} sx={{ color: 'white', width: 20, height: 20 }} />
                           </Button>
                        ),
                     }}
                  />
               </Grid>
            </Grid>

            <Grid container justifyContent="space-between">
               <Grid size={12}>
                  <InputField<RegisterInitialValues> label={t('formFields.companyName')} name="companyName" />
               </Grid>
            </Grid>
            <Grid container spacing="8px" justifyContent="space-between">
               <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                  <InputField<RegisterInitialValues> label={t('formFields.city')} name="city" />
               </Grid>
               <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                  <InputField<RegisterInitialValues> label={t('formFields.postCode')} name="postCode" />
               </Grid>
            </Grid>
            {values.country === 'Poland' ? (
               <Grid container spacing="8px" justifyContent="space-between">
                  <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                     <SelectField
                        searcher
                        noMinWidth
                        fullWidth
                        name="voivodeship"
                        inputVariant="outlined"
                        label={t('formFields.voivodeship')}
                        options={options.voivodeshipOptions}
                     />
                  </Grid>
                  <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                     <SelectField
                        disabled={!values.voivodeship}
                        searcher
                        noMinWidth
                        fullWidth
                        name="county"
                        inputVariant="outlined"
                        label={t('formFields.county')}
                        options={options.countyOptions}
                     />
                  </Grid>
               </Grid>
            ) : (
               <Grid container spacing="8px" justifyContent="space-between">
                  <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                     <InputField fullWidth name="voivodeship" label={t('formFields.voivodeship')} />
                  </Grid>
                  <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                     <InputField
                        disabled={!values.voivodeship}
                        fullWidth
                        name="county"
                        label={t('formFields.county')}
                     />
                  </Grid>
               </Grid>
            )}

            <Grid container spacing="8px" justifyContent="space-between">
               <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                  <InputField<RegisterInitialValues> label={t('formFields.street')} name="street" />
               </Grid>
               <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                  <InputField<RegisterInitialValues> label={t('formFields.houseNumber')} name="houseNumber" />
               </Grid>
            </Grid>
            <Grid container spacing="8px" mt="1px" justifyContent="space-between" alignItems="center">
               <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                  <InputField<RegisterInitialValues>
                     type="Number"
                     slotProps={{
                        htmlInput: {
                           min: 0,
                        },
                     }}
                     label={t('formFields.shopEmployees')}
                     name="shopEmployees"
                  />
               </Grid>
               <Grid size={{ lg: 6, md: 6, xs: 12 }}>
                  <InputField<RegisterInitialValues>
                     type="Number"
                     slotProps={{
                        htmlInput: {
                           min: 0,
                        },
                     }}
                     label={t('formFields.workshopEmployees')}
                     name="workshopEmployees"
                  />
               </Grid>
            </Grid>
            {/* <Button
               onClick={() => {
                  setFieldValue('county', undefined)
                  setFieldValue('voivodeship', undefined)
                  setFieldValue('country', undefined)
               }}
            >
               <KeyboardBackspaceIcon sx={{ mr: 2 }} /> {t('REGISTER:mainSite.backToCountrySelect')}
            </Button> */}
         </>
         {/* // ) : (
            // <Box
            //    sx={{
            //       display: 'flex',
            //       gap: 1,
            //       minHeight: 250,
            //       justifyContent: 'center',
            //       alignItems: 'center',
            //       flexDirection: 'column',
            //    }}
            // >
            //    <Typography variant="h3">{t('REGISTER:mainSite.selectCountry')}</Typography>
            //    {data &&
            //       Object.keys(data).map((key) => (
            //          <Button key={key} sx={{ fontSize: 16 }} onClick={() => setFieldValue('country', key)}>
            //             {data[key].name}
            //          </Button>
            //       ))}
            // </Box>
         // )} */}
      </Container>
   )
}
